import _ from "lodash";
import OneDrugManyPayerTypesChartControllerBase from "./one_drug_many_payer_types_chart_controller_base";
import shrinkWidgetToFit from "../../utilities/charts/shrink_widget_to_fit";
import {
  accumulateValuesFn,
  dataKeyForPayerType,
  toOneDecimal,
  percentString,
  withinOneDecimal,
} from "../../utilities/charts/chart_utils";
import showPayersModal from "../../utilities/charts/payers_modal";
import colors from "../../utilities/charts/colors";

export default class extends OneDrugManyPayerTypesChartControllerBase {
  static targets = ["title", "gridTitle", "gridFraction", "gridPercent", "gridChangeIndicator"];

  connect() {
    super.connect();
  }

  initializeParams() {
    const params = super.initializeParams();

    // Include all policy types (including empty string) but exclude empty from display
    this.enabledTypes = _.without(params.all_values, "");

    return params;
  }

  toggleCoverageMetricInternal() {
    this.renderChart();
  }

  togglePayerTypeInternal() {
    this.renderChart();
  }

  toggleDrugInternal() {
    this.renderChart();
  }

  updateShownPayersInternal() {
    this.renderChart();
  }

  togglePolicyType(event) {
    const policyType = $(event.target).data("policy-type");

    this.enabledTypes = _.xor(this.enabledTypes, [policyType]);

    this.renderChart();
  }

  renderChart() {
    this.setMainStats();
    this.renderBars();
    this.updateResponseVisibility();
    shrinkWidgetToFit(this.element, this.element.children[0]);
  }

  setMainStats() {
    $(this.titleTarget).text(`Published Policies for ${this.drug}`);
  }

  renderBars() {
    const policyTypes = _.without(this.params.all_values, "");
    const barColor = this.colorsByCoverageMetric[this.coverageMetric];

    policyTypes.forEach(policyType => {
      const target = this.gridTitleTargets.find(t => t.dataset.policyType === policyType);

      if (!target) return;

      const isEnabled = this.enabledTypes.includes(policyType);

      // Apply line-through style to disabled labels
      target.classList.toggle("line-through", !isEnabled);

      const { current, prior } = this.currentAndPriorDataForPolicyType(policyType);
      const { all: currentAll } = this.accumulateDataForShownPayers().current;
      const { all: priorAll } = this.accumulateDataForShownPayers().prior;

      // Update fraction display
      const fractionTarget = this.gridFractionTargets.find(
        t => t.dataset.policyType === policyType,
      );

      if (fractionTarget) {
        fractionTarget.innerText = isEnabled
          ? `${this.formatNumber(current.count)}/${this.formatNumber(currentAll)}`
          : "";
      }

      // Update percentage and bar
      const percentTarget = this.gridPercentTargets.find(t => t.dataset.policyType === policyType);

      if (percentTarget) {
        const currentPercent = current.count / currentAll || 0;
        const priorPercent = prior.count / priorAll || 0;

        const mainPercent = this.showChangesFromPrevious
          ? Math.max(currentPercent, priorPercent)
          : currentPercent;

        const textElement = percentTarget.querySelector(".text");
        const barElement = percentTarget.querySelector(".bar");
        const barRemainderElement = percentTarget.querySelector(".bar-remainder");

        if (textElement) {
          textElement.innerText = isEnabled ? percentString(current.count, currentAll) : "";
        }

        if (barElement) {
          barElement.style.transform = isEnabled ? `scaleX(${mainPercent})` : `scaleX(0)`;
          barElement.style.backgroundColor = barColor;
        }

        if (barRemainderElement) {
          barRemainderElement.style.transform = isEnabled
            ? `scaleX(${1 - mainPercent})`
            : `scaleX(0)`;
          barRemainderElement.classList.toggle("hidden", currentAll === 0 || !isEnabled);
        }
      }

      // Update change indicator if showing changes
      if (this.showChangesFromPrevious) {
        const changeTarget = this.gridChangeIndicatorTargets.find(
          t => t.dataset.policyType === policyType,
        );

        if (changeTarget) {
          const currentPercent = current.count / currentAll || 0;
          const priorPercent = prior.count / priorAll || 0;
          const arrow = changeTarget.querySelector("svg");

          if (withinOneDecimal(priorPercent, currentPercent)) {
            changeTarget.title = null;
            if (arrow) arrow.classList.add("hidden");
          } else if (currentPercent > priorPercent) {
            changeTarget.title = `Increased ${toOneDecimal((currentPercent - priorPercent) * 100)}`;
            if (arrow) {
              arrow.classList.remove("hidden", "text-red");
              arrow.classList.add("text-green");
              arrow.style.transform = "rotate(0deg)";
            }
          } else {
            changeTarget.title = `Decreased ${toOneDecimal((priorPercent - currentPercent) * 100)}`;
            if (arrow) {
              arrow.classList.remove("hidden", "text-green");
              arrow.classList.add("text-red");
              arrow.style.transform = "rotate(180deg)";
            }
          }
        }
      }
    });
  }

  currentAndPriorDataForPolicyType(policyType) {
    let currentCount = 0;

    let priorCount = 0;

    this.payerTypesShown.forEach(payerType => {
      const currentData = this.params.data[this.drug][payerType];
      const priorData = this.params.previous_data?.[this.drug]?.[payerType];

      const accumulate = accumulateValuesFn(payerType, this.coverageMetric);

      if (currentData && currentData[policyType]) {
        currentCount += accumulate(currentData[policyType]);
      }

      if (priorData && priorData[policyType]) {
        priorCount += accumulate(priorData[policyType]);
      }
    });

    return {
      current: { count: currentCount },
      prior: { count: priorCount },
    };
  }

  accumulateDataForShownPayers() {
    const output = {
      prior: { withPolicy: 0, all: 0 },
      current: { withPolicy: 0, all: 0 },
    };

    this.payerTypesShown.forEach(payerType => {
      const currentData = this.params.data[this.drug][payerType];
      const priorData = this.params.previous_data?.[this.drug]?.[payerType];

      const accumulate = accumulateValuesFn(payerType, this.coverageMetric);

      if (currentData) {
        // Include enabled policy types + empty string in calculations
        const enabledPolicyTypesWithEmpty = [...this.enabledTypes, ""];
        const currentWithPolicy = _.flatten(this.enabledTypes.map(type => currentData[type] || []));
        const currentAll = _.flatten(
          enabledPolicyTypesWithEmpty.map(type => currentData[type] || []),
        );

        output.current.withPolicy += accumulate(currentWithPolicy);
        output.current.all += accumulate(currentAll);
      }

      if (priorData) {
        // Include enabled policy types + empty string in calculations
        const enabledPolicyTypesWithEmpty = [...this.enabledTypes, ""];
        const priorWithPolicy = _.flatten(this.enabledTypes.map(type => priorData[type] || []));
        const priorAll = _.flatten(enabledPolicyTypesWithEmpty.map(type => priorData[type] || []));

        output.prior.withPolicy += accumulate(priorWithPolicy);
        output.prior.all += accumulate(priorAll);
      }
    });

    return output;
  }

  updateResponseVisibility() {
    // All policy types are always visible for published policies charts
  }

  formatNumber(count) {
    const suffix = this.coverageMetric === "lives" ? "m" : "";

    return `${toOneDecimal(count)}${suffix}`;
  }

  get colorsByCoverageMetric() {
    return {
      payers: colors.magenta,
      lives: colors.teal,
    };
  }

  onBarClick(event) {
    const policyType = $(event.target).data("policy-type");

    const title = `Payers with ${policyType} for ${this.drug} Published Policies`;

    const payerIds = {};
    const extraParams = {};

    this.payerTypesShown.forEach(payerType => {
      const normalizedPayerType = dataKeyForPayerType(payerType);

      const payerIdsFor = dataset =>
        dataset?.[this.drug]?.[payerType]?.[policyType]?.map(pl => pl.payer) || [];

      if (this.showChangesFromPrevious) {
        const currentPayers = payerIdsFor(this.params.data);
        const priorPayers = payerIdsFor(this.params.previous_data);

        if (currentPayers.length > 0 || priorPayers.length > 0) {
          payerIds[normalizedPayerType] = _.union(currentPayers, priorPayers);

          const changedPayers = _.xor(currentPayers, priorPayers);

          if (changedPayers.length > 0) {
            extraParams.changedCoverage = extraParams.changedCoverage || {};
            extraParams.changedCoverage[normalizedPayerType] = changedPayers.map(payer => ({
              payer,
              drug: this.drug,
            }));
          }
        }
      } else {
        const payers = payerIdsFor(this.params.data);

        if (payers.length > 0) {
          payerIds[normalizedPayerType] = payers;
        }
      }
    });

    if (this.showChangesFromPrevious) {
      extraParams.previousSnapshotSetId = this.params.previous_snapshot_set.id;
    }

    showPayersModal(this.drug, title, payerIds, extraParams);
  }

  onBarRemainderClick(event) {
    const policyType = $(event.target).data("policy-type");
    const otherPolicyTypes = _.without(this.params.all_values, policyType);

    const title = `Payers without ${policyType} for ${this.drug} Published Policies`;

    const payerIds = {};
    const extraParams = {};

    this.payerTypesShown.forEach(payerType => {
      const normalizedPayerType = dataKeyForPayerType(payerType);

      const payerIdsFor = dataset =>
        _.flatten(
          otherPolicyTypes.map(
            type => dataset?.[this.drug]?.[payerType]?.[type]?.map(pl => pl.payer) || [],
          ),
        );

      if (this.showChangesFromPrevious) {
        const currentPayers = payerIdsFor(this.params.data);
        const priorPayers = payerIdsFor(this.params.previous_data);

        if (currentPayers.length > 0 || priorPayers.length > 0) {
          payerIds[normalizedPayerType] = _.union(currentPayers, priorPayers);

          const changedPayers = _.xor(currentPayers, priorPayers);

          if (changedPayers.length > 0) {
            extraParams.changedCoverage = extraParams.changedCoverage || {};
            extraParams.changedCoverage[normalizedPayerType] = changedPayers.map(payer => ({
              payer,
              drug: this.drug,
            }));
          }
        }
      } else {
        const payers = payerIdsFor(this.params.data);

        if (payers.length > 0) {
          payerIds[normalizedPayerType] = payers;
        }
      }
    });

    if (this.showChangesFromPrevious) {
      extraParams.previousSnapshotSetId = this.params.previous_snapshot_set.id;
    }

    showPayersModal(this.drug, title, payerIds, extraParams);
  }
}
